import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Refresh,
  Storage,
  Memory,
  Speed,
  Cloud,
  WarningAmber,
} from "@mui/icons-material";

import {
  fetchSiteMetrics,
  clearSiteMetrics,
} from "../../redux/slices/siteSlice";

const formatMB = (mb) => {
  if (mb == null || isNaN(mb)) return "0 MB";
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${Math.round(mb * 100) / 100} MB`;
};

/**
 * Site performance tab with real-time metrics
 */
const SitePerformanceTab = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { siteMetrics, metricsLoading, metricsError } = useSelector(
    (state) => state.site,
  );
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (id) {
      dispatch(fetchSiteMetrics(id));
    }
    return () => {
      dispatch(clearSiteMetrics());
    };
  }, [dispatch, id, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const getContainerStatusColor = (status) => {
    switch (status) {
      case "running":
        return "success.main";
      case "stopped":
        return "error.main";
      default:
        return "warning.main";
    }
  };

  // Loading state
  if (metricsLoading && !siteMetrics) {
    return (
      <CardContent>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
        >
          <CircularProgress />
        </Box>
      </CardContent>
    );
  }

  const metrics = siteMetrics?.data?.metrics;

  // Error or no data
  if (metricsError || (!metricsLoading && !metrics)) {
    return (
      <CardContent>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
          textAlign="center"
        >
          <WarningAmber sx={{ fontSize: 64, color: "warning.main", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Performance Metrics Unavailable
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 400 }}
          >
            {(typeof metricsError === "string"
              ? metricsError
              : metricsError?.error || metricsError?.message) ||
              "Unable to retrieve performance metrics. The site containers may still be starting up."}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            Retry
          </Button>
        </Box>
      </CardContent>
    );
  }

  // Worker unavailable fallback
  if (metrics?.workerUnavailable) {
    return (
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight={600}>
            Site Overview
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label="WORKER OFFLINE" color="warning" size="small" />
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                disabled={metricsLoading}
                size="small"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Detailed metrics are temporarily unavailable because the worker server
          is offline.
        </Alert>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              icon={<Cloud color="primary" sx={{ fontSize: 32, mb: 1 }} />}
              value={metrics?.site?.status || "Unknown"}
              label="Site Status"
              color="primary.main"
              variant="h5"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              icon={<Storage color="info" sx={{ fontSize: 32, mb: 1 }} />}
              value={formatMB(metrics?.storage?.total?.mb)}
              label="Storage"
              color="info.main"
              variant="h5"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              icon={<Speed color="success" sx={{ fontSize: 32, mb: 1 }} />}
              value={metrics?.site?.environment || "N/A"}
              label="Environment"
              color="success.main"
              variant="h5"
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={metricsLoading}
          >
            Retry Full Metrics
          </Button>
        </Box>
      </CardContent>
    );
  }

  // Calculate storage values
  const totalMB = metrics?.storage?.total?.mb || 0;
  const filesMB = metrics?.storage?.files?.mb || 0;
  const dbMB = metrics?.storage?.database?.mb || 0;
  const filesPercent =
    totalMB > 0 ? Math.min((filesMB / totalMB) * 100, 100) : 0;
  const dbPercent = totalMB > 0 ? Math.min((dbMB / totalMB) * 100, 100) : 0;

  // Count running containers
  const containers = metrics?.containers || {};
  const containerEntries = Object.entries(containers);
  const runningCount = containerEntries.filter(
    ([, d]) => d.status === "running",
  ).length;

  // Determine overall health
  const allRunning =
    containerEntries.length > 0 && runningCount === containerEntries.length;
  const healthLabel = allRunning
    ? "HEALTHY"
    : runningCount > 0
      ? "DEGRADED"
      : "DOWN";
  const healthColor = allRunning
    ? "success"
    : runningCount > 0
      ? "warning"
      : "error";

  return (
    <CardContent>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight={600}>
          Performance & Health
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip label={healthLabel} color={healthColor} size="small" />
          <Tooltip title="Refresh Metrics">
            <IconButton
              onClick={handleRefresh}
              disabled={metricsLoading}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<Storage color="primary" sx={{ fontSize: 32, mb: 1 }} />}
            value={formatMB(totalMB)}
            label="Total Storage"
            color="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<Speed color="success" sx={{ fontSize: 32, mb: 1 }} />}
            value={formatMB(filesMB)}
            label="Site Files"
            color="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<Memory color="info" sx={{ fontSize: 32, mb: 1 }} />}
            value={formatMB(dbMB)}
            label="Database Size"
            color="info.main"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<Cloud color="secondary" sx={{ fontSize: 32, mb: 1 }} />}
            value={`${runningCount}/${containerEntries.length}`}
            label="Containers Running"
            color="secondary.main"
          />
        </Grid>
      </Grid>

      {/* Storage Breakdown */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Storage Breakdown
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Site Files (WordPress, Themes, Plugins, Uploads)
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatMB(filesMB)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={filesPercent}
              sx={{ height: 8, borderRadius: 4 }}
              color="primary"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Database (MySQL)
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatMB(dbMB)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={dbPercent}
              sx={{ height: 8, borderRadius: 4 }}
              color="secondary"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Container Health */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Container Health
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {containerEntries.map(([type, data]) => (
          <Grid size={{ xs: 12, md: 4 }} key={type}>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                borderLeft: 4,
                borderColor: getContainerStatusColor(data.status),
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2" textTransform="uppercase">
                  {type}
                </Typography>
                <Chip
                  label={data.status || "unknown"}
                  size="small"
                  color={data.status === "running" ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
              {data.startedAt && (
                <Typography variant="body2" color="text.secondary">
                  Started: {new Date(data.startedAt).toLocaleString()}
                </Typography>
              )}
              {data.error && (
                <Typography variant="body2" color="error.main">
                  {data.error}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Note */}
      <Box
        sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2, textAlign: "center" }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing real-time storage and container health metrics from your site.
        </Typography>
      </Box>
    </CardContent>
  );
};

const MetricCard = ({ icon, value, label, color, variant = "h4" }) => (
  <Box sx={{ textAlign: "center", p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
    {icon}
    <Typography variant={variant} fontWeight={700} color={color}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default SitePerformanceTab;
